import { filterRegions, filterStatus, filterYears, allGenres, type FilterOptions, type SortKey } from '@/utils/movies';

interface FilterBarProps {
  value: FilterOptions;
  onChange: (next: FilterOptions) => void;
  showGenre?: boolean;
}

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: 'hot', label: '按热度', icon: 'ri-fire-line' },
  { key: 'latest', label: '按上映时间', icon: 'ri-calendar-line' },
  { key: 'rating', label: '按评分', icon: 'ri-star-line' },
];

export default function FilterBar({ value, onChange, showGenre = false }: FilterBarProps) {
  const hasFilter = Boolean(value.region || value.year || value.genre || value.status);

  const renderGroup = (
    label: string,
    options: { key: string; label: string }[],
    field: 'region' | 'year' | 'status' | 'genre',
  ) => (
    <div className="flex items-start gap-3 py-2.5 border-b border-background-200 last:border-b-0">
      <span className="shrink-0 w-16 pt-1 text-sm text-foreground-500 font-label">{label}</span>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onChange({ ...value, [field]: '' })}
          className={`px-3 py-1 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
            !value[field]
              ? 'bg-primary-500 text-background-50'
              : 'bg-background-100 text-foreground-600 hover:bg-background-200'
          }`}
        >
          全部
        </button>
        {options.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => onChange({ ...value, [field]: option.key })}
            className={`px-3 py-1 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
              value[field] === option.key
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <i className="ri-filter-3-line text-accent-500 w-5 h-5 flex items-center justify-center"></i>
          <h2 className="font-heading font-bold text-sm text-foreground-950">筛选条件</h2>
        </div>
        {hasFilter && (
          <button
            type="button"
            onClick={() => onChange({ ...value, region: '', year: '', genre: '', status: '' })}
            className="text-sm text-accent-600 hover:text-accent-700 cursor-pointer flex items-center gap-1 whitespace-nowrap"
          >
            <i className="ri-refresh-line w-4 h-4 flex items-center justify-center"></i>
            重置筛选
          </button>
        )}
      </div>
      {renderGroup('地区', filterRegions.map((r) => ({ key: r, label: r })), 'region')}
      {renderGroup('年代', filterYears.map((y) => ({ key: y, label: y })), 'year')}
      {renderGroup('状态', filterStatus.map((s) => ({ key: s, label: s })), 'status')}
      {showGenre && renderGroup('类型', allGenres.map((g) => ({ key: g.key, label: g.name })), 'genre')}
      <div className="flex items-start gap-3 pt-3 mt-1 border-t border-background-200">
        <span className="shrink-0 w-16 pt-1 text-sm text-foreground-500 font-label">排序</span>
        <div className="flex flex-wrap gap-2">
          {sortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => onChange({ ...value, sort: option.key })}
              className={`px-3 py-1 rounded-full text-sm transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap ${
                (value.sort ?? 'hot') === option.key
                  ? 'bg-accent-500 text-primary-950'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${option.icon} w-4 h-4 flex items-center justify-center`}></i>
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}